// REFACTORED: 2026-02-02
// FROM src/components/project/Projects.tsx

// ============================================================================
// PROJECT CHARTS - Reusable chart components for project analytics
// ============================================================================

import React from "react";
import { Box, Card, CardContent, Typography, alpha } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { SEARCE_COLORS } from "../../theme";
import SegmentDonutChart, { type DonutSegmentData, INSIGHTS_DONUT_DIMENSIONS } from "./SegmentDonutChart";
import DonutInsightCardShell from "./DonutInsightCardShell";

// ============================================================================
// COMBINED METRICS AND STATUS CHART - Donut (5 statuses, callouts, center total)
// ============================================================================

interface CombinedMetricsAndStatusChartProps {
  metrics: any;
  statusCounts: any;
}

export const CombinedMetricsAndStatusChart: React.FC<CombinedMetricsAndStatusChartProps> = ({
  metrics: _metrics,
  statusCounts,
}) => {
  const chartData = [
    { name: "In Progress", value: statusCounts.active || 0, color: SEARCE_COLORS.primary },
    { name: "Yet to Start", value: statusCounts.yet_to_start || 0, color: SEARCE_COLORS.secondary },
    { name: "Completed", value: statusCounts.completed || 0, color: SEARCE_COLORS.success },
    { name: "On Hold", value: statusCounts.on_hold || 0, color: SEARCE_COLORS.warning },
    { name: "Cancelled", value: statusCounts.cancelled || 0, color: SEARCE_COLORS.error },
  ];

  const statusTotal = chartData.reduce((sum, item) => sum + item.value, 0);

  if (statusTotal === 0) {
    return (
      <DonutInsightCardShell title="Project Metrics">
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
          <Typography variant="body1" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </DonutInsightCardShell>
    );
  }

  return (
    <DonutInsightCardShell title="Project Metrics">
      <SegmentDonutChart
        data={chartData as DonutSegmentData[]}
        centerValue={statusTotal}
        centerLabel="Total Projects"
        tooltipUnitLabel="projects"
        {...INSIGHTS_DONUT_DIMENSIONS}
      />
    </DonutInsightCardShell>
  );
};

// ============================================================================
// PROJECTS BREAKDOWN CHART - Bar chart showing project distribution
// ============================================================================

interface ProjectsBreakdownChartProps {
  metrics: any;
}

export const ProjectsBreakdownChart: React.FC<ProjectsBreakdownChartProps> = ({ metrics }) => {
  const clientProjects = (metrics.total_projects || 0) - (metrics.internal_projects || 0);

  const chartData = [
    {
      name: "Total Projects",
      value: metrics.total_projects || 0,
      color: SEARCE_COLORS.primary,
    },
    {
      name: "Client Projects",
      value: clientProjects,
      color: SEARCE_COLORS.success,
    },
    {
      name: "Internal Projects",
      value: metrics.internal_projects || 0,
      color: SEARCE_COLORS.info,
    },
    
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          sx={{
            backgroundColor: SEARCE_COLORS.cardBackground,
            border: `1px solid ${SEARCE_COLORS.border}`,
            borderRadius: 2,
            p: 1.5,
            boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: SEARCE_COLORS.textPrimary }}>
            {data.payload.name}
          </Typography>
          <Typography variant="body2" sx={{ color: data.payload.color, fontWeight: 500 }}>
            Count: {data.value.toLocaleString()}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
          border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
          transition: "all 0.3s ease",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:hover": {
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body1" color="textSecondary">
            No data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
        border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        transition: "all 0.3s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            mb: 1.5,
          }}
        >
          Project Portfolio Overview
        </Typography>
        <Box sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          background: `linear-gradient(135deg, ${alpha(SEARCE_COLORS.primary, 0.03)} 0%, ${alpha(SEARCE_COLORS.primary, 0.01)} 100%)`,
          borderRadius: 2,
          p: 1.5,
        }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={280}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 12 }}
              />
              <YAxis
                tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 12 }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend - Below chart */}
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {chartData.map((item, index) => {
            return (
              <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "2px",
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: SEARCE_COLORS.textPrimary,
                    fontSize: "0.65rem",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.name}: {item.value.toLocaleString()}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// FUNDING SOURCE ANALYSIS — Donut (5 buckets, callouts, center = project count)
// ============================================================================

const FUNDING_SOURCE_ORDER: { label: string; color: string }[] = [
  { label: "Customer", color: SEARCE_COLORS.primary },
  { label: "Cloud Service Provider", color: SEARCE_COLORS.info },
  { label: "Unknown", color: SEARCE_COLORS.neutral_500 },
  { label: "Both", color: SEARCE_COLORS.secondary },
  { label: "Coupon", color: SEARCE_COLORS.warning },
];

function normalizeFundingBucket(raw: string): string {
  const s = (raw || "").trim().toLowerCase();
  if (!s || s === "unknown" || s === "n/a" || s === "-" || s === "null") return "Unknown";
  if (s.includes("coupon")) return "Coupon";
  if (s.includes("both")) return "Both";
  if (s.includes("cloud") || s.includes("csp") || (s.includes("service") && s.includes("provider")))
    return "Cloud Service Provider";
  if (s.includes("customer")) return "Customer";
  const exact = FUNDING_SOURCE_ORDER.find((f) => f.label.toLowerCase() === s);
  if (exact) return exact.label;
  return "Unknown";
}

interface FundedByBreakdownChartProps {
  fundingData: any[];
}

export const FundedByBreakdownChart: React.FC<FundedByBreakdownChartProps> = ({ fundingData }) => {
  const counts = new Map<string, number>();
  FUNDING_SOURCE_ORDER.forEach((b) => counts.set(b.label, 0));

  fundingData.forEach((item) => {
    const bucket = normalizeFundingBucket(String(item.funded_by ?? "Unknown"));
    counts.set(bucket, (counts.get(bucket) || 0) + (item.count || 0));
  });

  const chartData = FUNDING_SOURCE_ORDER.map(({ label, color }) => ({
    name: label,
    value: counts.get(label) || 0,
    color,
  }));

  const fundingTotal = chartData.reduce((sum, item) => sum + item.value, 0);

  if (fundingTotal === 0) {
    return (
      <DonutInsightCardShell title="Funding Source Analysis">
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
          <Typography variant="body1" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </DonutInsightCardShell>
    );
  }

  return (
    <DonutInsightCardShell title="Funding Source Analysis">
      <SegmentDonutChart
        data={chartData as DonutSegmentData[]}
        centerValue={fundingTotal}
        centerLabel="Projects"
        tooltipUnitLabel="projects"
        {...INSIGHTS_DONUT_DIMENSIONS}
      />
    </DonutInsightCardShell>
  );
};

// ============================================================================
// PROJECT STATUS DONUT CHART - Legacy donut chart (kept for compatibility)
// ============================================================================

interface ProjectStatusDonutChartProps {
  statusCounts: any;
}

export const ProjectStatusDonutChart: React.FC<ProjectStatusDonutChartProps> = ({ statusCounts }) => {
  // Prepare data for the donut chart
  const chartData = [
    {
      name: "In Progress",
      value: statusCounts.active || 0,
      color: SEARCE_COLORS.success,
    },
    {
      name: "Yet to Start",
      value: statusCounts.yet_to_start || 0,
      color: SEARCE_COLORS.info,
    },
    {
      name: "Completed",
      value: statusCounts.completed || 0,
      color: "gray",
    },
    {
      name: "On Hold",
      value: statusCounts.on_hold || 0,
      color: SEARCE_COLORS.warning,
    },
    {
      name: "Cancelled",
      value: statusCounts.cancelled || 0,
      color: SEARCE_COLORS.error,
    },
  ].filter((item) => item.value > 0); // Only show statuses with data

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : "0";
      return (
        <Box
          sx={{
            backgroundColor: SEARCE_COLORS.cardBackground,
            border: `1px solid ${SEARCE_COLORS.border}`,
            borderRadius: 2,
            p: 1.5,
            boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: SEARCE_COLORS.textPrimary }}>
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ color: data.payload.color, fontWeight: 500 }}>
            Count: {data.value.toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            Percentage: {percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (total === 0) {
    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.1)}`,
          border: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.5)}`,
        }}
      >
        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="textSecondary">
            No project status data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        border: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.5)}`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: SEARCE_COLORS.textPrimary,
          }}
        >
          Project Status Distribution
        </Typography>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
          {/* Donut Chart */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <Box
            sx={{
              flex: { xs: "none", md: "0 0 200px" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            {chartData.map((item, index) => {
              const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
              return (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: SEARCE_COLORS.textPrimary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: SEARCE_COLORS.textSecondary,
                        display: "block",
                      }}
                    >
                      {item.value.toLocaleString()} ({percentage}%)
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            <Box
              sx={{
                mt: 1,
                pt: 1.5,
                borderTop: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.3)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: SEARCE_COLORS.textPrimary,
                }}
              >
                Total: {total.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

